'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { searchSiteContentAction } from '@/lib/actions';
import type { SiteSearchResultItem } from '@/lib/types/site-search';
import SearchIcon from '../Icons/SearchIcon';
import ChevronDownIcon from '../Icons/ChevronDown';
import SpinnerIcon from '../Icons/SpinnerIcon';
import { useSearchParams } from 'next/navigation';
import { addTrailingSlashToInternalUrl } from '@/lib/utils/trailingSlash';

const PAGE_SIZE = 6;

type LoadingMode = 'idle' | 'search' | 'pagination';

export default function SiteSearchInteractive() {
  const searchParams = useSearchParams();
  const urlQuery = searchParams.get('s')?.trim() ?? '';

  const [query, setQuery] = useState(urlQuery);
  const [submittedQuery, setSubmittedQuery] = useState(urlQuery);
  const [results, setResults] = useState<SiteSearchResultItem[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loadingMode, setLoadingMode] = useState<LoadingMode>('idle');
  const [hasSearched, setHasSearched] = useState(Boolean(urlQuery));
  const [currentPage, setCurrentPage] = useState(1);
  const [searchError, setSearchError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const isLoading = loadingMode !== 'idle';
  const isPaginating = loadingMode === 'pagination';

  const scrollResultsToTop = useCallback(() => {
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const runSearch = useCallback(
    async (searchQuery: string, page: number, mode: 'search' | 'pagination') => {
      setLoadingMode(mode);

      if (mode === 'search') {
        setResults([]);
        setTotalResults(0);
        setCurrentPage(1);
        setSearchError(null);
      }

      try {
        const response = await searchSiteContentAction(searchQuery, page, PAGE_SIZE);
        setResults(response.items);
        setTotalResults(response.total);
        setCurrentPage(response.page);

        if (mode === 'pagination') {
          scrollResultsToTop();
        }
      } catch (error) {
        console.error('[SiteSearchInteractive] Search failed:', error);

        if (mode === 'search') {
          setResults([]);
          setTotalResults(0);
          setSearchError('Search is temporarily unavailable. Please try again.');
        }
      } finally {
        setLoadingMode('idle');
      }
    },
    [scrollResultsToTop],
  );

  useEffect(() => {
    if (!urlQuery) return;

    const timeoutId = window.setTimeout(() => {
      void runSearch(urlQuery, 1, 'search');
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [urlQuery, runSearch]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalResults / PAGE_SIZE));
  }, [totalResults]);

  const visiblePages = useMemo(() => {
    const groupStart = Math.floor((currentPage - 1) / 5) * 5 + 1;
    const groupEnd = Math.min(groupStart + 4, totalPages);

    return Array.from({ length: groupEnd - groupStart + 1 }, (_, index) => groupStart + index);
  }, [currentPage, totalPages]);

  const hasMorePagesAfterVisibleRange = visiblePages[visiblePages.length - 1] < totalPages;

  const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmed = query.trim();
    if (!trimmed) return;

    setHasSearched(true);
    setSubmittedQuery(trimmed);
    await runSearch(trimmed, 1, 'search');
  };

  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  return (
    <div
      ref={rootRef}
      className='w-full'
    >
      <form
        onSubmit={handleSubmit}
        role='search'
        className='flex h-[60px] w-full items-center rounded-[8px] border-2 border-[#D8D6D2] bg-white p-[8px] shadow-none'
      >
        <input
          type='text'
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder='Search'
          aria-label='Search our site'
          style={{ caretColor: 'var(--search-caret-color, var(--color-brand-accent))' }}
          className='h-full w-full flex-1 border-0 bg-transparent px-4 text-[16px] leading-[140%] text-primary-500 outline-none placeholder:text-primary-500/45'
        />

        <button
          id='site-search-submit'
          type='submit'
          aria-label='Submit site search'
          disabled={isLoading || !query.trim()}
          className='flex h-[46px] w-[46px] shrink-0 cursor-pointer items-center justify-center rounded-[10px] bg-primary-btn-bg text-primary-btn-text transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70'
        >
          <SearchIcon className='h-[18px] w-[18px] text-primary-btn-text' />
        </button>
      </form>

      {hasSearched && (
        <div className='relative left-1/2 mt-10 w-screen -translate-x-1/2 bg-white'>
          <div className='mx-auto w-full max-w-[1248px] px-6 py-10 lg:px-12 lg:py-12'>
            <div className='mx-auto w-full max-w-[642px]'>
              {isLoading && !isPaginating ? (
                <div className='flex items-center justify-center py-8'>
                  <SpinnerIcon className='h-8 w-8 animate-spin text-brand-accent' />
                  <span className='sr-only'>Searching...</span>
                </div>
              ) : results.length > 0 ? (
                <>
                  <div
                    className={`flex flex-col transition-opacity duration-200 ${
                      isPaginating ? 'opacity-60' : 'opacity-100'
                    }`}
                    aria-busy={isPaginating}
                  >
                    {results.map((item, index) => (
                      <article
                        key={`${item.href}-${index}`}
                        className='border-b-2 border-[#D8D6D2] py-10 first:pt-0 last:border-b-0 last:pb-0'
                      >
                        <div className='grid gap-3'>
                          <h3 className='text-[18px] font-semibold leading-[110%] text-primary-500 lg:text-[20px] lg:font-medium lg:leading-[130%]'>
                            {item.title}
                          </h3>

                          <p className='text-[18px] font-normal leading-[130%] tracking-[0.18px] text-primary-500'>
                            {item.excerpt}
                          </p>

                          <Link
                            href={addTrailingSlashToInternalUrl(item.href)}
                            aria-label={`Learn more about ${item.title}`}
                            className='inline-block text-[14px] font-semibold leading-[130%] tracking-[0.14px] text-primary-500 underline decoration-brand-accent decoration-[1.5px] underline-offset-[2px]'
                          >
                            Learn More
                            {item.title ? (
                              <span className='sr-only'> about {item.title}</span>
                            ) : null}
                          </Link>
                        </div>
                      </article>
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className='relative'>
                      {isPaginating && (
                        <div className='absolute -top-14 left-1/2 -translate-x-1/2'>
                          <SpinnerIcon className='h-7 w-7 animate-spin text-brand-accent' />
                        </div>
                      )}

                      <nav
                        aria-label='Search results pagination'
                        className='mx-auto mt-26 flex w-full max-w-90 items-center justify-center gap-6.5 text-[18px] font-semibold leading-7 lg:mt-40 lg:max-w-none lg:gap-5'
                      >
                        <button
                          type='button'
                          onClick={() =>
                            canGoPrev && runSearch(submittedQuery, currentPage - 1, 'pagination')
                          }
                          disabled={!canGoPrev || isLoading}
                          className={`flex items-center gap-2 whitespace-nowrap ${
                            canGoPrev && !isLoading
                              ? 'cursor-pointer text-primary-500'
                              : 'cursor-not-allowed text-[#757575]'
                          }`}
                        >
                          <ChevronDownIcon className='h-[12px] w-[12px] rotate-90 text-current' />
                          <span>Prev</span>
                        </button>

                        <div className='flex items-center gap-[26px] lg:gap-5'>
                          {visiblePages.map((page, index) => {
                            const isActive = page === currentPage;
                            const isLastVisiblePage = index === visiblePages.length - 1;
                            const showTrailingEllipsis =
                              isLastVisiblePage && hasMorePagesAfterVisibleRange;

                            return (
                              <div
                                key={page}
                                className='flex items-center gap-0 whitespace-nowrap'
                              >
                                <button
                                  type='button'
                                  onClick={() => runSearch(submittedQuery, page, 'pagination')}
                                  disabled={isLoading}
                                  aria-label={`Go to page ${page}`}
                                  aria-current={isActive ? 'page' : undefined}
                                  className={
                                    isActive
                                      ? 'flex h-[40px] w-[40px] items-center justify-center rounded-full bg-brand-accent text-[18px] font-semibold leading-[28px] text-brand-accent-text'
                                      : isLoading
                                        ? 'cursor-not-allowed text-[18px] font-semibold leading-[28px] text-[#757575]'
                                        : 'cursor-pointer text-[18px] font-semibold leading-[28px] text-primary-500'
                                  }
                                >
                                  {page}
                                </button>

                                {showTrailingEllipsis && (
                                  <span className='text-[18px] font-semibold leading-[28px] text-primary-500'>
                                    ...
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        <button
                          type='button'
                          onClick={() =>
                            canGoNext && runSearch(submittedQuery, currentPage + 1, 'pagination')
                          }
                          disabled={!canGoNext || isLoading}
                          className={`flex items-center gap-2 whitespace-nowrap ${
                            canGoNext && !isLoading
                              ? 'cursor-pointer text-primary-500'
                              : 'cursor-not-allowed text-[#757575]'
                          }`}
                        >
                          <span>Next</span>
                          <ChevronDownIcon className='h-[12px] w-[12px] -rotate-90 text-current' />
                        </button>
                      </nav>
                    </div>
                  )}
                </>
              ) : searchError ? (
                <div className='text-primary-500'>
                  <p className='text-[18px] font-semibold leading-[130%]'>Search unavailable</p>
                  <p className='mt-2 text-[16px] leading-[150%]'>{searchError}</p>
                </div>
              ) : (
                <div className='text-primary-500'>
                  <p className='text-[18px] font-semibold leading-[130%]'>No results found</p>
                  <p className='mt-2 text-[16px] leading-[150%]'>
                    {`No results were found for "${submittedQuery}".`}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
